import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { formatRut, formatDate, formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  User,
  Home,
  Briefcase,
  FileText,
  Truck,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Loader2,
  Edit,
  Users,
} from 'lucide-react';

const BeneficiaryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [beneficiary, setBeneficiary] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [beneficiaryRes, deliveriesRes] = await Promise.all([
          api.get(`/beneficiaries/${id}`),
          api.get('/deliveries', { params: { beneficiary_id: id } }),
        ]);
        setBeneficiary(beneficiaryRes.data);
        setDeliveries(deliveriesRes.data);
      } catch (error) {
        toast.error('Error al cargar datos del beneficiario');
        navigate('/beneficiaries');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!beneficiary) {
    return null;
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Activo</Badge>;
      case 'inactive':
        return <Badge variant="secondary">Inactivo</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pendiente</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6" data-testid="beneficiary-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/beneficiaries')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
              {beneficiary.name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-slate-500">{formatRut(beneficiary.rut)}</span>
              {getStatusBadge(beneficiary.status)}
            </div>
          </div>
        </div>
        <Button variant="outline" data-testid="edit-btn">
          <Edit className="w-4 h-4 mr-2" />
          Editar
        </Button>
      </div>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-teal-50 text-teal-600">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Edad</p>
              <p className="font-semibold text-slate-900">{beneficiary.age} años</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Comuna</p>
              <p className="font-semibold text-slate-900">{beneficiary.comuna}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-600">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Tramo RSH</p>
              <p className="font-semibold text-slate-900">{beneficiary.rsh_tramo || 'No registrado'}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Entregas</p>
              <p className="font-semibold text-slate-900">{deliveries.length} total</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="personal" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="personal" className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">Personal</span>
          </TabsTrigger>
          <TabsTrigger value="housing" className="flex items-center gap-2">
            <Home className="w-4 h-4" />
            <span className="hidden sm:inline">Vivienda</span>
          </TabsTrigger>
          <TabsTrigger value="socioeconomic" className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            <span className="hidden sm:inline">Socioeconómico</span>
          </TabsTrigger>
          <TabsTrigger value="deliveries" className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            <span className="hidden sm:inline">Historial</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="personal" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Datos Personales</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Nombre Completo</p>
                    <p className="font-medium text-slate-900">{beneficiary.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">RUT</p>
                    <p className="font-medium text-slate-900">{formatRut(beneficiary.rut)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Fecha de Nacimiento</p>
                    <p className="font-medium text-slate-900">{formatDate(beneficiary.birth_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Nacionalidad</p>
                    <p className="font-medium text-slate-900">{beneficiary.nationality}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Estado Civil</p>
                    <p className="font-medium text-slate-900">{beneficiary.marital_status || 'No especificado'}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Folio RSH</p>
                    <p className="font-medium text-slate-900">{beneficiary.rsh_folio || 'No registrado'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Tramo RSH</p>
                    <p className="font-medium text-slate-900">{beneficiary.rsh_tramo || 'No registrado'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <p className="font-medium text-slate-900">{beneficiary.phone}</p>
                  </div>
                  {beneficiary.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <p className="font-medium text-slate-900">{beneficiary.email}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Family Members */}
              {beneficiary.family_members && beneficiary.family_members.length > 0 && (
                <div className="mt-8">
                  <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Grupo Familiar
                  </h4>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nombre</TableHead>
                          <TableHead>RUT</TableHead>
                          <TableHead>Parentesco</TableHead>
                          <TableHead>Edad</TableHead>
                          <TableHead>Ocupación</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {beneficiary.family_members.map((member, index) => (
                          <TableRow key={index}>
                            <TableCell className="font-medium">{member.name}</TableCell>
                            <TableCell>{member.rut ? formatRut(member.rut) : '-'}</TableCell>
                            <TableCell>{member.relationship}</TableCell>
                            <TableCell>{member.age} años</TableCell>
                            <TableCell>{member.occupation || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="housing" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Información de Vivienda</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Dirección</p>
                    <p className="font-medium text-slate-900">{beneficiary.address}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Comuna</p>
                    <p className="font-medium text-slate-900">{beneficiary.comuna}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Tipo de Vivienda</p>
                    <p className="font-medium text-slate-900">{beneficiary.housing_type || 'No especificado'}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Tenencia</p>
                    <p className="font-medium text-slate-900">{beneficiary.housing_ownership || 'No especificado'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Servicios Básicos</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {beneficiary.basic_services && beneficiary.basic_services.length > 0 ? (
                        beneficiary.basic_services.map(service => (
                          <Badge key={service} variant="secondary">{service}</Badge>
                        ))
                      ) : (
                        <span className="text-slate-400">No especificado</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="socioeconomic" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Manrope']">Datos Socioeconómicos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Nivel Educacional</p>
                    <p className="font-medium text-slate-900">{beneficiary.education_level || 'No especificado'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Situación Laboral</p>
                    <p className="font-medium text-slate-900">{beneficiary.employment_status || 'No especificado'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Ingreso Personal</p>
                    <p className="font-medium text-slate-900">{formatCurrency(beneficiary.personal_income)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Ingreso Hogar</p>
                    <p className="font-medium text-slate-900">{formatCurrency(beneficiary.household_income)}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500">Motivo de Vulnerabilidad</p>
                    <p className="font-medium text-slate-900">{beneficiary.vulnerability_reason || 'No especificado'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Beneficios Actuales</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {beneficiary.current_benefits && beneficiary.current_benefits.length > 0 ? (
                        beneficiary.current_benefits.map(benefit => (
                          <Badge key={benefit} variant="secondary">{benefit}</Badge>
                        ))
                      ) : (
                        <span className="text-slate-400">Ninguno registrado</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deliveries" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-['Manrope']">Historial de Entregas</CardTitle>
              <Button asChild className="bg-teal-600 hover:bg-teal-700" data-testid="new-delivery-btn">
                <Link to="/deliveries">
                  <Truck className="w-4 h-4 mr-2" />
                  Nueva Entrega
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {deliveries.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Truck className="w-12 h-12 mx-auto mb-4" />
                  <p>No hay entregas registradas</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Tipo de Ayuda</TableHead>
                        <TableHead>Productos</TableHead>
                        <TableHead>Entregado por</TableHead>
                        <TableHead>Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deliveries.map((delivery) => (
                        <TableRow key={delivery.delivery_id}>
                          <TableCell>{formatDate(delivery.created_at)}</TableCell>
                          <TableCell>{delivery.aid_type}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {delivery.items.slice(0, 2).map((item, index) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {item.product_name} x{item.quantity}
                                </Badge>
                              ))}
                              {delivery.items.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{delivery.items.length - 2} más
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{delivery.delivered_by}</TableCell>
                          <TableCell>
                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                              {delivery.status === 'completed' ? 'Completada' : delivery.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BeneficiaryDetail;
